import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { PLANS } from '@/data/plans';

const PlansSection = () => {
  const { plans: apiPlans, loading, loadPlans } = useSubscription();

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const plans = Object.values(PLANS).map((planData) => {
    let priceDisplay: string;
    if (planData.code === 'free') {
      priceDisplay = 'Grátis';
    } else if (loading || apiPlans.length === 0) {
      priceDisplay = '...';
    } else {
      const apiPlan = apiPlans.find((p) => p.code === planData.code);
      const priceCents = apiPlan?.price_cents ?? 0;
      priceDisplay = priceCents > 0
        ? `R$ ${(priceCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        : '...';
    }

    return {
      ...planData,
      price: priceDisplay,
      cta: planData.code === 'free' ? 'Começar grátis' : `Assinar ${planData.name}`,
      href: planData.code === 'free' ? '/auth?mode=register' : '/plans',
    };
  });

  return (
    <section className="bg-[#0D0C09] py-28 px-6 md:px-10">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <span className="inline-block text-[#C8A96E] text-xs uppercase tracking-widest mb-4">Planos</span>
        <h2 className="text-4xl md:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
          Simples. <strong>Transparente.</strong>
        </h2>
        <p className="text-white/40 text-sm mt-4 max-w-md mx-auto">
          Comece gratuitamente e escale conforme seu ministério cresce.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 ${
              plan.highlight
                ? 'bg-[#C8A96E] text-[#0A0A0F]'
                : 'bg-[#111111] border border-white/5 text-white hover:border-white/15'
            }`}
          >
            {plan.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0A0A0F] text-[#C8A96E] text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
                Mais popular
              </div>
            )}

            <div className="mb-6">
              <p className={`text-xs uppercase tracking-widest mb-3 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>
                {plan.name}
              </p>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{plan.price}</span>
                <span className={`text-sm mb-1 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>{plan.period}</span>
              </div>
              <p className={`text-sm leading-relaxed ${plan.highlight ? 'text-[#0A0A0F]/70' : 'text-white/40'}`}>{plan.description}</p>
            </div>

            <div className="flex-1 mb-8">
              <div className="flex flex-col gap-3">
                {plan.features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <div className={`w-4 h-4 flex items-center justify-center`}>
                      <i className={`ri-check-line text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-[#C8A96E]'}`}></i>
                    </div>
                    <span className={`text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-white/70'}`}>{f}</span>
                  </div>
                ))}
                {plan.disabled.map((f) => (
                  <div key={f} className="flex items-center gap-3 opacity-40">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line text-sm"></i>
                    </div>
                    <span className="text-sm line-through">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link
              to={plan.href}
              className={`w-full py-3 rounded-full text-sm font-semibold text-center transition-all duration-300 whitespace-nowrap cursor-pointer ${
                plan.highlight
                  ? 'bg-[#0A0A0F] text-white hover:bg-[#1A1A1A]'
                  : 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
              }`}
            >
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

export default PlansSection;
